import {
  Button, Collapsible, EmptyInput, flattenErrorTypeArray, Flex, FlexColumn, InputRow, ModelSelect, ModelSelectSearchInput, ModelTextarea, SecondaryButton,
  useAsyncFunctionProgress,
  useModelFormUpdate,
  UserRoles,
  useUserHasRole,
} from '@equifinance/shared';
import styled from 'styled-components';
import { useMemo } from 'react';
import { useAccountContext } from '../../../contexts';
import {
  Category, Stage, Task, useTasksContext,
} from '../../../contexts/tasks';

const TaskCreationContainer = styled(FlexColumn)`${({ theme }) => `
  padding: ${theme.padding[3]} 0;
  width: 100%;
`}`;

const TaskFormContainer = styled(FlexColumn)`${({ theme }) => `
  padding: 0 ${theme.padding[9]};
  gap: ${theme.padding[3]};
`}`;

const TaskCreationBtns = styled(Flex)`${({ theme }) => `
  padding: ${theme.padding[3]} 0;
  justify-content: end;
  gap: ${theme.padding[2]};
  margin-top: ${theme.padding[2]};
`}`;

export default function TaskCreation({ task, taskIndex }: { task: Task, taskIndex: number }) {
  const { taskTypes, tasks, cancelTask } = useTasksContext();
  const isUnderwriter = useUserHasRole(UserRoles.UNDERWRITER);
  const { errors } = tasks;
  const { account: { applicants } } = useAccountContext();
  const { type: taskTypeId } = task;
  const update = useModelFormUpdate();
  const path = `tasks[${taskIndex}]`;

  const typeOptions = taskTypes.reduce((accum, { taskTypeId, title }) => ({ ...accum, [taskTypeId]: title }), {} as Record<string, string>);
  const selectedCategory = taskTypes.find((t) => (t.taskTypeId === taskTypeId))?.category;

  const categoryOptionsByType: Task['category'][] = useMemo(() => {
    if (selectedCategory === 'Application') return ['Application'];
    if (selectedCategory === 'Applicant') return applicants.length > 1 ? ['Applicant1', 'Applicant2'] : ['Applicant1'];
    return applicants.length > 1 ? ['Application', 'Applicant1', 'Applicant2'] : ['Application', 'Applicant1'];
  }, [taskTypeId, taskTypes]);

  const categoryOptions = categoryOptionsByType.reduce((accum, current) => ({ ...accum, [current]: Category[current] }), {} as Record<string, string>);

  const showCategorySelection = selectedCategory === 'Applicant' || taskTypeId === 'Miscellaneous';
  const showDescriptionTxtArea = taskTypeId === 'Miscellaneous';

  const showStageSelection = useUserHasRole(UserRoles.UNDERWRITER) && taskTypeId;
  const stageOptions = Object.keys(Stage)?.reduce((acc, current) => ({ ...acc, [current]: current }), {});

  const isCreateTaskDisabled = flattenErrorTypeArray(errors?.[task.id] ?? []).length > 0;

  const { inProgress, execute: executeCancelTask } = useAsyncFunctionProgress(() => cancelTask(task.id));

  const onCreateTask = (isTaskForUW = false) => {
    const status = isTaskForUW ? 'SubmittedToLender' : 'OutstandingForBroker';
    update({
      [`${path}.status`]: status,
    });
  };

  return (
    <TaskCreationContainer>
      <Collapsible
        title="Task details"
        level={2}
        titleIcon={<></>}
      >
        <TaskFormContainer>
          <InputRow>
            <ModelSelectSearchInput
              label="Type"
              field={`${path}.type`}
              options={typeOptions}
              required
              placeholder="Please select"
            />

            {showStageSelection ? (
              <ModelSelect
                label="Before"
                field={`${path}.stage`}
                options={stageOptions}
                required
                placeholder="Select"
              />
            ) : (<EmptyInput />)}
          </InputRow>

          {showCategorySelection && (
            <InputRow>
              <ModelSelect
                label="Category"
                field={`${path}.category`}
                options={categoryOptions}
                disabled={Object.entries(categoryOptions).length === 1}
                required
                placeholder="Please select"
              />
              <EmptyInput />
            </InputRow>
          )}

          {showDescriptionTxtArea && (
            <InputRow>
              <ModelTextarea
                label="Description"
                field={`${path}.description`}
                required
                rows={0}
                minHeight={62}
                maxHeight={140}
              />
            </InputRow>
          )}

        </TaskFormContainer>
      </Collapsible>
      <TaskCreationBtns>
        <SecondaryButton disabled={inProgress} onClick={executeCancelTask} small>
          Cancel
        </SecondaryButton>

        <Button onClick={() => onCreateTask()} small disabled={isCreateTaskDisabled}>
          {isUnderwriter ? 'Create task for Broker' : 'Create task'}
        </Button>

        {isUnderwriter && (
          <Button onClick={() => onCreateTask(true)} small disabled={isCreateTaskDisabled}>
            Create task for UW
          </Button>
        )}
      </TaskCreationBtns>
    </TaskCreationContainer>
  );
}
